import PQueue from "p-queue"
import { clamp } from "@/utils/math"
import { Sentry } from "@/telemetry/sentry"
import { registerSentryContextTarget } from "@/telemetry/sentry-peers"
import type { PixelData } from "@/core/utils/image-data"
import { Deferred } from "@/utils/deferred"
import {
	encodeTIFF as encodeTIFFBlob,
	encodeTIFFData as encodeTIFFPayload,
	getTIFFBufferByteLengths,
	type EncodedTIFFData,
	type TiffEncoderLike,
	type TiffEncodeOptions,
} from "@/core/encoders/tiff-encoder"

export interface TiffEncoderOptions {
	jobs: number
}

interface TiffBufferReservationOptions {
	pixelByteLength: number
	scratchByteLength: number
}

export interface TiffRGBDataBufferLease extends TiffEncoderLike {
	data: Uint8Array | Uint16Array
	bitDepth: 8 | 16
	encodeTIFFData(pixelData: PixelData, options?: TiffEncodeOptions): Promise<EncodedTIFFData>
	release(): Promise<void>
}

interface TiffArena {
	pixelBuffer: ArrayBuffer
	scratchBuffer: ArrayBuffer
}

class TiffEncoderController implements TiffEncoderLike {
	public readonly maxConcurrency = 2

	private queue = new PQueue()
	private concurrency = 1
	private arenas: TiffArena[] = []
	private idleArenas: TiffArena[] = []

	public constructor() {
		this.setOptions({ jobs: 1 })
	}

	public setSentryContext() {
		// The TIFF controller is main-thread only for now; this exists so it can be
		// registered beside JPEG and later keep the same public shape when workers land.
	}

	public disableSentry() {
		// No worker Sentry state to disable yet.
	}

	public setOptions(options: TiffEncoderOptions) {
		this.concurrency = clamp(options.jobs, 1, this.maxConcurrency)
		this.queue.clear()
		this.queue = new PQueue({ concurrency: this.concurrency })
		this.queue.on("error", (e) => Sentry.captureException(e))
		this.resetArenas()
	}

	public clearQueue() {
		this.queue.clear()
		this.resetArenas()
	}

	public releaseBuffers() {
		if (this.idleArenas.length !== this.arenas.length) {
			return
		}

		this.resetArenas()
	}

	public reserveBuffers(options: TiffBufferReservationOptions) {
		if (this.idleArenas.length !== this.arenas.length) {
			throw new Error("Cannot reserve TIFF buffers while an export is active")
		}

		for (const arena of this.arenas) {
			if (arena.pixelBuffer.byteLength < options.pixelByteLength) {
				arena.pixelBuffer = new ArrayBuffer(options.pixelByteLength)
			}
			if (arena.scratchBuffer.byteLength < options.scratchByteLength) {
				arena.scratchBuffer = new ArrayBuffer(options.scratchByteLength)
			}
		}
	}

	public async encodeTIFF(pixelData: PixelData, options?: TiffEncodeOptions): Promise<Blob> {
		return await this.queue.add(
			async () => {
				const arena = this.acquireArena()
				try {
					const { scratchByteLength } = getTIFFBufferByteLengths({
						width: pixelData.width,
						height: pixelData.height,
						bitDepth: pixelData.data instanceof Uint16Array ? 16 : 8,
						rowsPerStrip: options?.rowsPerStrip,
						targetStripByteLength: options?.targetStripByteLength,
					})
					this.ensureArenaScratchCapacity(arena, scratchByteLength)
					return await encodeTIFFBlob(pixelData, {
						...options,
						scratchBufferProvider: (byteLength) =>
							this.getArenaScratchView(arena, byteLength),
					})
				} finally {
					this.releaseArena(arena)
				}
			},
			{ timeout: 30000 },
		)
	}

	public async acquireRGBDataBuffer(options: {
		pixelByteLength: number
		bitDepth: 8 | 16
		scratchByteLength: number
	}): Promise<TiffRGBDataBufferLease> {
		const leaseReady = new Deferred<TiffRGBDataBufferLease>()
		const leaseReleased = new Deferred<void>()
		const leaseFreed = new Deferred<void>()

		void this.queue
			.add(async () => {
				const arena = this.acquireArena()

				try {
					this.ensureArenaPixelCapacity(arena, options.pixelByteLength)
					this.ensureArenaScratchCapacity(arena, options.scratchByteLength)
					const data = this.getArenaPixelView(
						arena,
						options.pixelByteLength,
						options.bitDepth,
					)
					let released = false

					const release = async () => {
						if (released) {
							await leaseFreed.promise
							return
						}

						released = true
						leaseReleased.resolve()
						await leaseFreed.promise
					}

					leaseReady.resolve({
						data,
						bitDepth: options.bitDepth,
						release,
						encodeTIFF: async (pixelData, encodeOptions) =>
							await encodeTIFFBlob(pixelData, {
								...encodeOptions,
								scratchBufferProvider: (byteLength) =>
									this.getArenaScratchView(arena, byteLength),
							}),
						encodeTIFFData: (pixelData, encodeOptions) =>
							Promise.resolve(
								encodeTIFFPayload(pixelData, {
									...encodeOptions,
									scratchBufferProvider: (byteLength) =>
										this.getArenaScratchView(arena, byteLength),
								}),
							),
					})

					await leaseReleased.promise
				} catch (error) {
					leaseReady.reject(error)
					throw error
				} finally {
					this.releaseArena(arena)
					leaseFreed.resolve()
				}
			})
			.catch((error) => {
				leaseReady.reject(error)
			})

		return await leaseReady.promise
	}

	public getTotalBytesUsage() {
		return Promise.resolve(
			this.arenas.reduce(
				(total, arena) =>
					total + arena.pixelBuffer.byteLength + arena.scratchBuffer.byteLength,
				0,
			),
		)
	}

	private resetArenas() {
		this.arenas = Array.from({ length: this.concurrency }, () => ({
			pixelBuffer: new ArrayBuffer(0),
			scratchBuffer: new ArrayBuffer(0),
		}))
		this.idleArenas = [...this.arenas]
	}

	private acquireArena() {
		const arena = this.idleArenas.pop()
		if (!arena) {
			throw new Error("TIFF encoder arena unavailable")
		}

		return arena
	}

	private releaseArena(arena: TiffArena) {
		this.idleArenas.push(arena)
	}

	private ensureArenaPixelCapacity(arena: TiffArena, byteLength: number) {
		if (byteLength > arena.pixelBuffer.byteLength) {
			throw new Error("TIFF pixel arena is smaller than the requested export buffer")
		}
	}

	private ensureArenaScratchCapacity(arena: TiffArena, byteLength: number) {
		if (byteLength > arena.scratchBuffer.byteLength) {
			throw new Error("TIFF scratch arena is smaller than the requested strip")
		}
	}

	private getArenaPixelView(arena: TiffArena, byteLength: number, bitDepth: 8 | 16) {
		this.ensureArenaPixelCapacity(arena, byteLength)

		if (bitDepth === 16) {
			return new Uint16Array(arena.pixelBuffer, 0, byteLength >> 1)
		}

		return new Uint8Array(arena.pixelBuffer, 0, byteLength)
	}

	private getArenaScratchView(arena: TiffArena, byteLength: number) {
		this.ensureArenaScratchCapacity(arena, byteLength)

		return new Uint8Array(arena.scratchBuffer, 0, byteLength)
	}
}

export const TiffEncoder = new TiffEncoderController()

TiffEncoder.setOptions({ jobs: 1 })
registerSentryContextTarget(TiffEncoder)
